import { z } from "zod";

// =============================================================================
// SPECIAL VALUES
// =============================================================================

// NOTE (Kevin, 2025-12-19, REPL-21177): Marker for values that should be resolved from DOM at runtime.
export const COMPUTE_VALUE = "__COMPUTE__" as const;
export type ComputeValue = typeof COMPUTE_VALUE;

// =============================================================================
// ACTION MODIFIERS
// =============================================================================

export const deleteModifierValueSchema = z
  .object({
    type: z.literal("delete"),
    value: z.any(),
  })
  .describe("DeleteModifierValue");

// =============================================================================
// PRODUCT MODIFIERS
// =============================================================================

export const productModifierValueSchema = z
  .object({
    type: z.literal("product"),
    value: z.string(),
  })
  .describe("ProductModifierValue");

export const variantModifierValueSchema = z
  .object({
    type: z.literal("variant"),
    value: z.string(),
  })
  .describe("VariantModifierValue");

// =============================================================================
// TEXT & TYPOGRAPHY MODIFIERS
// =============================================================================

export const textContentModifierValueSchema = z
  .object({
    type: z.literal("content"),
    value: z.string(),
    className: z.string().optional(),
  })
  .describe("TextContentModifierValue");

export const textAlignmentModifierValueSchema = z
  .object({
    type: z.literal("alignment"),
    value: z.enum(["left", "center", "right", "justify"]),
  })
  .describe("TextAlignmentModifierValue");

export const textFontWeightModifierValueSchema = z
  .object({
    type: z.literal("fontWeight"),
    value: z.number(),
  })
  .describe("TextFontWeightModifierValue");

export const textFontSizeModifierValueSchema = z
  .object({
    type: z.literal("fontSize"),
    value: z.number().nullable(),
  })
  .describe("TextFontSizeModifierValue");

export const textLineHeightModifierValueSchema = z
  .object({
    type: z.literal("lineHeight"),
    value: z.number().nullable(),
  })
  .describe("TextLineHeightModifierValue");

export const textSpacingModifierValueSchema = z
  .object({
    type: z.literal("textSpacing"),
    value: z.number().nullable(),
  })
  .describe("TextSpacingModifierValue");

export const fontFamilyModifierValueSchema = z
  .object({
    type: z.literal("fontFamily"),
    value: z.string(),
  })
  .describe("FontFamilyModifierValue");

export const textShadowModifierValueSchema = z
  .object({
    type: z.literal("textShadow"),
    value: z.object({
      offsetX: z.number(),
      offsetY: z.number(),
      blurRadius: z.number().optional(),
      color: z.string().optional(),
    }),
  })
  .describe("TextShadowModifierValue");

export const textTagModifierValueSchema = z
  .object({
    type: z.literal("textTag"),
    // NOTE (Nick, 2025-12-18): "div" is used when exiting style library mode to reset to default tag
    value: z.enum(["h1", "h2", "h3", "h4", "p", "div"]),
  })
  .describe("TextTagModifierValue");

// =============================================================================
// LAYOUT & SPACING MODIFIERS
// =============================================================================

export const paddingModifierValueSchema = z
  .object({
    type: z.literal("padding"),
    direction: z.enum(["top", "right", "bottom", "left"]),
    value: z.number(),
  })
  .describe("PaddingModifierValue");

export const marginModifierValueSchema = z
  .object({
    type: z.literal("margin"),
    direction: z.enum(["top", "right", "bottom", "left"]),
    value: z.union([z.number(), z.literal("auto")]),
  })
  .describe("MarginModifierValue");

export const dimensionModifierValueSchema = z
  .object({
    type: z.literal("dimension"),
    side: z.enum(["width", "height"]),
    value: z.union([z.number(), z.enum(["auto", "full"])]),
  })
  .describe("DimensionModifierValue");

export const boxShadowModifierValueSchema = z
  .object({
    type: z.literal("boxShadow"),
    value: z.enum(["none", "2xs", "xs", "sm", "md", "lg", "xl", "2xl"]),
  })
  .describe("BoxShadowModifierValue");

export const directionModifierValueSchema = z
  .object({
    type: z.literal("containerDirection"),
    value: z.enum(["row", "column"]),
  })
  .describe("DirectionModifierValue");

export const containerAlignmentModifierValueSchema = z
  .object({
    type: z.literal("containerAlignment"),
    value: z.enum(["start", "center", "end"]),
  })
  .describe("ContainerAlignmentModifierValue");

export const gapModifierValueSchema = z
  .object({
    type: z.literal("containerGap"),
    value: z.number(),
  })
  .describe("GapModifierValue");

// =============================================================================
// APPEARANCE & VISUAL MODIFIERS
// =============================================================================

export const opacityModifierValueSchema = z
  .object({
    type: z.literal("opacity"),
    value: z.number(),
  })
  .describe("OpacityModifierValue");

export const colorModifierValueSchema = z
  .object({
    type: z.literal("color"),
    value: z.string().nullable(),
  })
  .describe("ColorModifierValue");

export const backgroundColorModifierValueSchema = z
  .object({
    type: z.literal("backgroundColor"),
    value: z.string().nullable(),
  })
  .describe("BackgroundColorModifierValue");

export const borderColorModifierValueSchema = z
  .object({
    type: z.literal("borderColor"),
    value: z.string().nullable(),
  })
  .describe("BorderColorModifierValue");

export const borderStyleModifierValueSchema = z
  .object({
    type: z.literal("borderStyle"),
    value: z.enum(["none", "solid", "dashed", "dotted", "double"]),
  })
  .describe("BorderStyleModifierValue");

export const borderWidthModifierValueSchema = z
  .object({
    type: z.literal("borderWidth"),
    direction: z.enum(["top", "right", "bottom", "left"]),
    value: z.number(),
  })
  .describe("BorderWidthModifierValue");

export const cornerRadiusModifierValueSchema = z
  .object({
    type: z.literal("cornerRadius"),
    corner: z.enum(["topLeft", "topRight", "bottomRight", "bottomLeft"]),
    value: z.union([z.number(), z.literal("full")]),
  })
  .describe("CornerRadiusModifierValue");

// =============================================================================
// ASSET MODIFIERS
// =============================================================================

export const assetModifierValueSchema = z
  .object({
    type: z.literal("asset"),
    value: z.string(),
  })
  .describe("AssetModifierValue");

// NOTE (Kevin, 2025-12-16): Object-fit modifier for controlling how images/videos fit their container.
// Values correspond to CSS object-fit property.
export const objectFitModifierValueSchema = z
  .object({
    type: z.literal("objectFit"),
    value: z.enum(["fill", "contain", "cover", "none", "scale-down"]),
  })
  .describe("ObjectFitModifierValue");

/** Aspect-ratio modifier. "auto" means no constraint (Custom in UI). */
export const aspectRatioModifierValueSchema = z
  .object({
    type: z.literal("aspectRatio"),
    value: z.enum(["auto", "1/1", "3/2", "4/3", "4/5", "16/9"]),
  })
  .describe("AspectRatioModifierValue");

// =============================================================================
// LINK MODIFIERS
// =============================================================================

export const hrefModifierValueSchema = z
  .object({
    type: z.literal("href"),
    value: z.string(),
  })
  .describe("HrefModifierValue");

// =============================================================================
// ELEMENT-SPECIFIC MODIFIER UNIONS
// =============================================================================

export const textBlockModifierValueSchema = z
  .discriminatedUnion("type", [
    deleteModifierValueSchema,
    textContentModifierValueSchema,
    textAlignmentModifierValueSchema,
    colorModifierValueSchema,
    paddingModifierValueSchema,
    textShadowModifierValueSchema,
    textFontWeightModifierValueSchema,
    textFontSizeModifierValueSchema,
    textLineHeightModifierValueSchema,
    textSpacingModifierValueSchema,
    fontFamilyModifierValueSchema,
    textTagModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("TextBlockModifierValue");

export const imageModifierValueSchema = z
  .discriminatedUnion("type", [
    deleteModifierValueSchema,
    dimensionModifierValueSchema,
    marginModifierValueSchema,
    boxShadowModifierValueSchema,
    assetModifierValueSchema,
    objectFitModifierValueSchema,
    aspectRatioModifierValueSchema,
    borderColorModifierValueSchema,
    borderStyleModifierValueSchema,
    borderWidthModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("ImageModifierValue");

export const containerModifierValueSchema = z
  .discriminatedUnion("type", [
    directionModifierValueSchema,
    containerAlignmentModifierValueSchema,
    gapModifierValueSchema,
    paddingModifierValueSchema,
    marginModifierValueSchema,
    backgroundColorModifierValueSchema,
    borderColorModifierValueSchema,
    borderStyleModifierValueSchema,
    borderWidthModifierValueSchema,
    cornerRadiusModifierValueSchema,
    opacityModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("ContainerModifierValue");

export const buttonModifierValueSchema = z
  .discriminatedUnion("type", [
    deleteModifierValueSchema,
    textContentModifierValueSchema,
    textAlignmentModifierValueSchema,
    colorModifierValueSchema,
    paddingModifierValueSchema,
    textShadowModifierValueSchema,
    textFontWeightModifierValueSchema,
    textFontSizeModifierValueSchema,
    textLineHeightModifierValueSchema,
    textSpacingModifierValueSchema,
    fontFamilyModifierValueSchema,
    textTagModifierValueSchema,
    boxShadowModifierValueSchema,
    backgroundColorModifierValueSchema,
    borderColorModifierValueSchema,
    borderStyleModifierValueSchema,
    borderWidthModifierValueSchema,
    cornerRadiusModifierValueSchema,
    opacityModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("ButtonModifierValue");

export const iconModifierValueSchema = z
  .discriminatedUnion("type", [
    deleteModifierValueSchema,
    colorModifierValueSchema,
    paddingModifierValueSchema,
    productModifierValueSchema,
    variantModifierValueSchema,
  ])
  .describe("IconModifierValue");

export const universalModifierValueSchema = z
  .union([
    textBlockModifierValueSchema,
    imageModifierValueSchema,
    containerModifierValueSchema,
    buttonModifierValueSchema,
    iconModifierValueSchema,
    hrefModifierValueSchema,
  ])
  .describe("UniversalModifierValue");

export const elementSelectorSchema = z
  .object({
    stableElementId: z.string(),
  })
  .describe("ElementSelector");

export const selectedElementDynamicDataSourcePayloadSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("product"),
      productId: z.string(),
      variantId: z.string().optional(),
    }),
  ])
  .describe("SelectedElementDynamicDataSource");

export const modifiableElementTypeSchema = z
  .enum([
    "text",
    "container",
    "image",
    "video",
    "product-section",
    "button",
    "unknown",
    "link",
    "icon",
  ])
  .describe("ModifiableElementType");

// Schema for nested styleOverrides structure from scaffold (iframe)
const styleOverridesFromScaffoldSchema = z
  .record(
    z.string(),
    z.object({
      type: z.enum(["default", "styleLibrary"]),
      styleDefinition: z.record(
        z.string(),
        z.union([z.string(), z.null(), z.undefined()]),
      ),
    }),
  )
  .describe("StyleOverridesFromScaffold");

// Schema for flattened styleOverrides after transformation (used in editor store)
const flattenedStyleOverridesSchema = z
  .object({
    // Text styles
    fontSize: z.number().nullable().optional(),
    fontWeight: z.number().nullable().optional(),
    fontFamily: z.string().nullable().optional(),
    color: z.string().nullable().optional(),
    lineHeight: z.number().nullable().optional(),
    letterSpacing: z.number().nullable().optional(),
    textTransform: z.string().nullable().optional(),
    fontStyle: z.string().nullable().optional(),
    textAlign: z.string().nullable().optional(),
    textDecoration: z.string().nullable().optional(),
    textDecorationLine: z.string().nullable().optional(),
    // Size and spacing
    width: z.string().nullable().optional(),
    height: z.string().nullable().optional(),
    paddingTop: z.number().nullable().optional(),
    paddingRight: z.number().nullable().optional(),
    paddingBottom: z.number().nullable().optional(),
    paddingLeft: z.number().nullable().optional(),
    marginTop: z
      .union([z.number(), z.literal("auto")])
      .nullable()
      .optional(),
    marginRight: z
      .union([z.number(), z.literal("auto")])
      .nullable()
      .optional(),
    marginBottom: z
      .union([z.number(), z.literal("auto")])
      .nullable()
      .optional(),
    marginLeft: z
      .union([z.number(), z.literal("auto")])
      .nullable()
      .optional(),
    // Container styles
    flexDirection: z.string().nullable().optional(),
    justifyContent: z.string().nullable().optional(),
    alignItems: z.string().nullable().optional(),
    gap: z.number().nullable().optional(),
    display: z.string().nullable().optional(),
    // Background and appearance
    backgroundColor: z.string().nullable().optional(),
    opacity: z.number().nullable().optional(),
    // Corner radius
    cornerTopLeftRadius: z.number().nullable().optional(),
    cornerTopRightRadius: z.number().nullable().optional(),
    cornerBottomRightRadius: z.number().nullable().optional(),
    cornerBottomLeftRadius: z.number().nullable().optional(),
    // Border styles
    borderTopColor: z.string().nullable().optional(),
    borderRightColor: z.string().nullable().optional(),
    borderLeftColor: z.string().nullable().optional(),
    borderBottomColor: z.string().nullable().optional(),
    borderTopWidth: z.number().nullable().optional(),
    borderRightWidth: z.number().nullable().optional(),
    borderBottomWidth: z.number().nullable().optional(),
    borderLeftWidth: z.number().nullable().optional(),
    borderTopStyle: z.string().nullable().optional(),
    borderRightStyle: z.string().nullable().optional(),
    borderBottomStyle: z.string().nullable().optional(),
    borderLeftStyle: z.string().nullable().optional(),
    // Box shadow
    boxShadow: z.string().nullable().optional(),
  })
  .describe("FlattenedStyleOverrides");

// Schema for payload coming FROM scaffold (nested format)
export const selectedElementDesignationElementSchema = z
  .object({
    type: z.literal("default"),
    element: z.object({
      type: modifiableElementTypeSchema,
      stableElementId: z.string(),
      innerHtml: z.string(),
      tagName: z.string(),
      props: z
        .record(z.string(), z.unknown())
        .and(
          z
            .object({
              className: z.string().nullable(),
              src: z.string().nullable(),
            })
            .partial(),
        )
        .nullable(),
      // styleOverrides comes from scaffold as nested structure
      styleOverrides: styleOverridesFromScaffoldSchema,
    }),
  })
  .describe("SelectedElementDesignationElement");

// Schema for transformed payload used IN editor (flattened format)
export const selectedElementDesignationElementTransformedSchema = z
  .object({
    type: z.literal("default"),
    element: z.object({
      type: modifiableElementTypeSchema,
      stableElementId: z.string(),
      innerHtml: z.string(),
      tagName: z.string(),
      props: z
        .record(z.string(), z.unknown())
        .and(
          z
            .object({
              className: z.string().nullable(),
              src: z.string().nullable(),
            })
            .partial(),
        )
        .nullable(),
      // styleOverrides after transformation is flattened
      styleOverrides: flattenedStyleOverridesSchema,
    }),
  })
  .describe("SelectedElementDesignationElementTransformed");

// Original schema for data coming FROM the scaffold
export const selectedElementDesignationSchema = z
  .discriminatedUnion("type", [
    selectedElementDesignationElementSchema,
    z.object({
      type: z.literal("link"),
      href: z.string().nullish(),
      target: z.string().nullish(),
      element: z.object({
        stableElementId: z.string(),
      }),
    }),
    z.object({
      type: z.literal("dynamicDataSource"),
      data: selectedElementDynamicDataSourcePayloadSchema,
      element: z.object({
        stableElementId: z.string(),
      }),
    }),
  ])
  .describe("SelectedElementDesignation");

// Transformed schema for data used IN the editor (with flattened styleOverrides)
export const selectedElementDesignationTransformedSchema = z
  .discriminatedUnion("type", [
    selectedElementDesignationElementTransformedSchema,
    z.object({
      type: z.literal("link"),
      href: z.string().nullish(),
      target: z.string().nullish(),
      element: z.object({
        stableElementId: z.string(),
      }),
    }),
    z.object({
      type: z.literal("dynamicDataSource"),
      data: selectedElementDynamicDataSourcePayloadSchema,
      element: z.object({
        stableElementId: z.string(),
      }),
    }),
  ])
  .describe("SelectedElementDesignationTransformed");

// Payload from scaffold (nested styleOverrides)
export const fiberSelectedPayloadSchema = z
  .object({
    selectedElementDesignations: z.array(selectedElementDesignationSchema),
  })
  .describe("FiberSelectedPayload");

export const modifierActionSchema = z
  .object({
    elementSelector: elementSelectorSchema,
    modifier: universalModifierValueSchema,
  })
  .describe("ModifierAction");

export const modifyBatchRequestSchema = z
  .object({
    projectId: z.string().uuid(),
    fileId: z.string().uuid(),
    actions: z.array(modifierActionSchema).min(1),
  })
  .describe("ModifyBatchRequest");

// Payload for editor store (flattened styleOverrides)
export const fiberSelectedPayloadTransformedSchema = z
  .object({
    selectedElementDesignations: z.array(
      selectedElementDesignationTransformedSchema,
    ),
  })
  .describe("FiberSelectedPayloadTransformed");
